import React, { useEffect, useRef } from 'react';
import { useState } from 'react';
// @ts-ignore
import styles from './SearchMedicamentRecord.module.scss';
import SearchMedicamentRecordPatientsData from './SearchMedicamentRecordPatientsData';
import NavigationStepper, {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
	NavigationStepperRef,
} from '../../Stepper/NavigationStepper';
import { SearchMedicamentRecordPatientFormValues } from './SearchMedicamentRecordPatientsDataFormScheme';
import SearchMedicamentRecordAuthorization, {
	SearchMedicamentRecordAuthorizationSource,
} from './SearchMedicamentRecordAuthorization';
import { useEffectAsync } from '../../../helpers';
import { checkSUKLCredentialsAvailability } from '../../../Modules/EPrescription/Services/SUKLCredentials.service';
import Loading from '../../Loading';
import SearchMedicamentRecordList from './SearchMedicamentRecordList/SearchMedicamentRecordList';
import type { SearchMedicamentRecordListItemMedicamentScheme } from './SearchMedicamentRecordList/SearchMedicamentRecordListItemMedicament';
import type { SearchMedicamentRecordPatientFormValues } from './SearchMedicamentRecordPatientsDataFormScheme';

interface SearchMedicamentRecordProps {
	patientId?: string;
	setIsLoading?: (isLoading: boolean) => void;
	onIssueAgainMedicament?: (
		medicament: SearchMedicamentRecordListItemMedicamentScheme,
		filterData: SearchMedicamentRecordPatientFormValues,
	) => void;
}

interface StepData extends SearchMedicamentRecordPatientFormValues {
	step?: string;
}

export default function SearchMedicamentRecord(
	props: SearchMedicamentRecordProps,
) {
	const { patientId, setIsLoading, onIssueAgainMedicament } = props;
	const [steps, setSteps] = useState<NavigationStepperItemType[]>([]);
	const [initialStep, setInitialStep] = useState('patientsData');
	const [data, setData] = useState<StepData | undefined>(undefined);
	const [isInitialized, setIsInitialized] = useState(true);
	const navigatorRef = useRef<NavigationStepperRef>(null);
	const onIssueAgainMedicamentRef = useRef(onIssueAgainMedicament);
	onIssueAgainMedicamentRef.current = onIssueAgainMedicament;

	const handleContinue = (
		formValues: SearchMedicamentRecordPatientFormValues,
		params: NavigationStepperComponentParams,
	) => {
		setData(() => ({ ...formValues, step: 'medicaments' }));
	};

	useEffect(() => {
		if (data?.step && navigatorRef?.current) {
			navigatorRef.current.setActiveStepPath(data?.step);
		}
	}, [data]);

	const handleBack = (params: NavigationStepperComponentParams) => {
		setData(undefined);
		params.setActiveStepPath('patientsData');
	};

	const handleSUKLWrongCredentials = () => {
		setIsInitialized(false);
		setIsInitialized(true);
	};

	useEffectAsync(async () => {
		setIsLoading?.(true);
		const tempSteps = [];

		const isMissingSUKLCredentials = await checkSUKLCredentialsAvailability();

		if (isMissingSUKLCredentials) {
			setInitialStep('authorization');
			tempSteps.push({
				name: 'base.verifyOfAccess',
				key: 'authorization',
				component: (params: NavigationStepperComponentParams) => (
					<SearchMedicamentRecordAuthorization
						source={SearchMedicamentRecordAuthorizationSource.LEKOVY_ZAZNAM}
						setIsLoading={params.setIsLoading}
						onSuccess={() => params.setActiveStepPath('patientsData')}
					/>
				),
			});
		}

		tempSteps.push(
			{
				name: 'patientTreatment.prescription.searchMedicalRecord.patientsData.title',
				key: 'patientsData',
				component: (params: NavigationStepperComponentParams) => (
					<SearchMedicamentRecordPatientsData
						onSuccess={(formValues: any) => handleContinue(formValues, params)}
						patientId={patientId}
						defaultValues={data}
					/>
				),
			},
			{
				name: 'patientTreatment.prescription.searchMedicalRecord.medicaments.title',
				key: 'medicaments',
				component: (params: NavigationStepperComponentParams) => (
					<SearchMedicamentRecordList
						filterData={data}
						onError={() => handleBack(params)}
						setLoading={params.setIsLoading}
						isLoading={params.isLoading}
						onSUKLWrongCredentials={handleSUKLWrongCredentials}
						onIssueAgain={(m, filterData) =>
							onIssueAgainMedicamentRef.current?.(m, filterData)
						}
					/>
				),
			},
		);

		setSteps(tempSteps);
		setIsLoading?.(false);
	}, [data, isInitialized]);

	if (!isInitialized) {
		return <Loading />;
	}

	return (
		<NavigationStepper
			ref={navigatorRef}
			steps={steps}
			initialStepPath={initialStep}
			canSkip={false}
		/>
	);
}
